import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload,
  Camera,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  ArrowRight,
  RefreshCw,
  Trash2,
  X,
  Image as ImageIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import { listItems, patchItem } from "@/lib/api";
import { BUILD_VERSION } from "@/lib/version";
import {
  uploadOne,
  validateFile,
  type UploadProgress,
} from "@/lib/upload";
import {
  stitchImagesToPdf,
  defaultScanFilename,
  pdfBlobToFile,
} from "@/lib/scan-stitch";
import { queryKeys } from "@/lib/queryKeys";
import type { IhubItem, ItemState } from "@/lib/types";
import { ReviewDialog } from "@/components/review-dialog";
import { MultiScanModal } from "@/components/multi-scan-modal";
import { useSession, useStartAuth } from "@/hooks/use-session";

export const Route = createFileRoute("/_app/ihub")({
  component: IHubView,
});

// ──────────────────────────────────────────────────────────────────────
// Action tiles
// ──────────────────────────────────────────────────────────────────────

interface ActionTileProps {
  icon: typeof Upload;
  title: string;
  subtitle: string;
  accept: string;
  multiple?: boolean;
  capture?: "environment" | "user";
  onFiles: (files: FileList) => void;
}

function ActionTile({
  icon: Icon,
  title,
  subtitle,
  accept,
  multiple,
  capture,
  onFiles,
}: ActionTileProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition"
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <div className="font-medium text-sm">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{subtitle}</div>
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        capture={capture}
        hidden
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            onFiles(e.target.files);
            // Reset so picking the same file twice in a row fires onChange.
            e.target.value = "";
          }
        }}
      />
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────
// State badge
// ──────────────────────────────────────────────────────────────────────

function StateBadge({ state }: { state: ItemState }) {
  const map: Record<
    ItemState,
    { label: string; cls: string; Icon: typeof Loader2; spin?: boolean }
  > = {
    NEW: {
      label: "Uploading",
      cls: "bg-muted text-muted-foreground border-border",
      Icon: Loader2,
      spin: true,
    },
    PROCESSING: {
      label: "Processing",
      cls: "bg-warning/15 text-warning-foreground border-warning/30",
      Icon: Loader2,
      spin: true,
    },
    REVIEW: {
      label: "Ready to review",
      cls: "bg-success/15 text-success border-success/30",
      Icon: CheckCircle2,
    },
    CONFIRMED: {
      label: "Filing to Drive",
      cls: "bg-primary/15 text-primary border-primary/30",
      Icon: Loader2,
      spin: true,
    },
    FILED: {
      label: "Filed",
      cls: "bg-success/15 text-success border-success/30",
      Icon: CheckCircle2,
    },
    FAILED_OCR: {
      label: "OCR failed",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      Icon: AlertTriangle,
    },
    FAILED_CLASSIFY: {
      label: "Classification failed",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      Icon: AlertTriangle,
    },
    FAILED_DRIVE_UPLOAD: {
      label: "Drive upload failed",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      Icon: AlertTriangle,
    },
    FAILED_DRIVE_AUTH: {
      label: "Drive sign-in expired",
      cls: "bg-destructive/10 text-destructive border-destructive/30",
      Icon: AlertTriangle,
    },
  };
  const m = map[state];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        m.cls,
      )}
    >
      <m.Icon className={cn("h-3 w-3", m.spin && "animate-spin")} />
      {m.label}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Item card
// ──────────────────────────────────────────────────────────────────────

// Meta keys are surfaced through their own badges on the card (subtype
// pill, confidence dot) and a dedicated notes textarea in the review
// dialog. Excluding them from the brief keeps the inline preview tight
// and avoids "confidence: medium" or "ocr_chars: 918" cluttering the
// card.
const META_FIELD_KEYS = new Set([
  "subtype",
  "confidence",
  "notes",
  "rotation",
  "ocr_chars",
  "kind",
]);

function fieldsBrief(json: string | null, max = 130): string {
  if (!json) return "";
  try {
    const obj = JSON.parse(json) as Record<string, unknown>;
    const pairs: string[] = [];
    for (const [k, v] of Object.entries(obj)) {
      if (META_FIELD_KEYS.has(k)) continue;
      if (v == null || v === "") continue;
      const str =
        typeof v === "string"
          ? v
          : typeof v === "number" || typeof v === "boolean"
            ? String(v)
            : JSON.stringify(v);
      pairs.push(`${k.replace(/_/g, " ")}: ${str}`);
    }
    const joined = pairs.join(" · ");
    return joined.length > max ? joined.slice(0, max - 1) + "…" : joined;
  } catch {
    return "";
  }
}

/**
 * Extract subtype + confidence from fields_json for badge rendering
 * on the inbox card. Returns nulls when the field is missing.
 */
function extractMeta(json: string | null): {
  subtype: string | null;
  confidence: "high" | "medium" | "low" | null;
} {
  if (!json) return { subtype: null, confidence: null };
  try {
    const obj = JSON.parse(json) as Record<string, unknown>;
    const subtype =
      typeof obj.subtype === "string" && obj.subtype.trim()
        ? obj.subtype.trim()
        : null;
    const c =
      typeof obj.confidence === "string" ? obj.confidence.trim().toLowerCase() : "";
    const confidence =
      c === "high" || c === "medium" || c === "low"
        ? (c as "high" | "medium" | "low")
        : null;
    return { subtype, confidence };
  } catch {
    return { subtype: null, confidence: null };
  }
}

function ItemCard({
  item,
  onOpenReview,
  selected,
  onToggleSelect,
}: {
  item: IhubItem;
  onOpenReview: (item: IhubItem) => void;
  selected: boolean;
  onToggleSelect: (file_id: string) => void;
}) {
  const qc = useQueryClient();
  const versionRef = useRef(item.version);
  useEffect(() => {
    versionRef.current = item.version;
  }, [item.version]);

  const retry = useMutation({
    mutationFn: () =>
      patchItem(item.file_id, {
        event: "RETRY",
        expected_version: versionRef.current,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ihub"] }),
  });

  const del = useMutation({
    mutationFn: () =>
      patchItem(item.file_id, {
        event: "USER_DELETE",
        expected_version: versionRef.current,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ihub"] }),
  });

  const borderTone =
    item.state === "REVIEW"
      ? "border-l-success"
      : item.state === "PROCESSING" || item.state === "CONFIRMED" || item.state === "NEW"
        ? "border-l-warning"
        : item.state === "FILED"
          ? "border-l-success"
          : "border-l-destructive";

  const brief =
    item.state === "REVIEW" || item.state === "FILED"
      ? fieldsBrief(item.fields_json)
      : null;
  const meta =
    item.state === "REVIEW" || item.state === "FILED"
      ? extractMeta(item.fields_json)
      : { subtype: null, confidence: null };
  const ago = Math.max(0, Math.floor(Date.now() / 1000) - item.state_changed_at);
  const agoLabel =
    ago < 60 ? `${ago}s ago` : ago < 3600 ? `${Math.floor(ago / 60)}m ago` : `${Math.floor(ago / 3600)}h ago`;

  return (
    <article
      className={cn(
        "rounded-xl bg-card border border-border border-l-4 p-5 transition hover:shadow-sm",
        borderTone,
        // Visual indicator when selected for bulk action.
        selected && "ring-2 ring-primary/40 bg-primary/[0.03]",
      )}
    >
      <div className="flex items-start gap-3">
        {/* Selection checkbox + file icon. The checkbox doubles as
            the file affordance — tapping anywhere on the icon
            area toggles selection, and a visible filetype glyph
            sits behind so the row still reads as "a file". */}
        <label
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground cursor-pointer hover:bg-muted/70 transition relative group"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${item.name}`}
        >
          {selected ? (
            <Checkbox
              checked
              onCheckedChange={() => onToggleSelect(item.file_id)}
              aria-label="Selected"
              className="h-5 w-5"
            />
          ) : (
            <>
              <FileText className="h-5 w-5 group-hover:opacity-30 transition" />
              <Checkbox
                checked={false}
                onCheckedChange={() => onToggleSelect(item.file_id)}
                aria-label="Select"
                className="h-5 w-5 absolute inset-0 m-auto opacity-0 group-hover:opacity-100 transition"
              />
            </>
          )}
        </label>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-medium text-sm truncate flex items-center gap-2">
              <span className="truncate">{item.name}</span>
              {/* Subtype badge — small primary-tinted pill showing the
                  doc's sub-classification (e.g. "Endowment", "80G").
                  Renders only when summarize picked one. */}
              {meta.subtype && (
                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/10 text-primary uppercase tracking-wide">
                  {meta.subtype}
                </span>
              )}
              {/* Confidence dot — green/amber/red. Hover reveals the
                  meaning. Hidden on low-density mobile but kept on
                  desktop. */}
              {meta.confidence && (
                <span
                  className={
                    "shrink-0 inline-block h-2 w-2 rounded-full " +
                    (meta.confidence === "high"
                      ? "bg-emerald-500"
                      : meta.confidence === "low"
                        ? "bg-red-500"
                        : "bg-amber-500")
                  }
                  title={
                    meta.confidence === "high"
                      ? "High confidence"
                      : meta.confidence === "low"
                        ? "Low confidence — please verify"
                        : "Medium confidence"
                  }
                  aria-hidden
                />
              )}
            </h3>
            <div className="flex items-center gap-2">
              <StateBadge state={item.state} />
              {/* Per-item delete on the inbox list. Surface the trash
                  icon on every card regardless of state so the user
                  doesn't have to open the review dialog just to remove
                  a misfire. window.confirm before the destructive
                  patch — no undo. */}
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive transition disabled:opacity-40"
                disabled={del.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  if (
                    window.confirm(
                      `Delete "${item.name}"?\n\nThis cannot be undone.`,
                    )
                  ) {
                    del.mutate();
                  }
                }}
                aria-label={`Delete ${item.name}`}
                title="Delete this item"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {(item.state === "PROCESSING" || item.state === "NEW") && (
            <p className="text-xs text-muted-foreground mt-2">
              {item.state === "PROCESSING" ? (
                <>
                  OCR <span className="text-success">{item.ocr_done_at ? "✓" : "…"}</span>{" "}
                  · AI{" "}
                  <span className="text-success">{item.ai_done_at ? "✓" : "…"}</span>
                </>
              ) : (
                <>Uploaded {agoLabel}</>
              )}
              <span className="ml-2">{agoLabel}</span>
            </p>
          )}

          {item.state === "REVIEW" && (
            <>
              {brief && (
                <p className="text-sm text-foreground/80 mt-2 leading-relaxed">
                  {brief}
                </p>
              )}
              <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground">
                  {item.suggested_folder ? (
                    <>
                      Suggested:{" "}
                      <span className="text-foreground font-medium">
                        {item.suggested_folder}
                      </span>
                    </>
                  ) : null}
                </div>
                <Button size="sm" className="gap-1.5" onClick={() => onOpenReview(item)}>
                  Open to review <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </>
          )}

          {item.state === "CONFIRMED" && (
            <p className="text-xs text-muted-foreground mt-2">
              Filing to <strong>{item.chosen_folder}</strong>…
            </p>
          )}

          {item.state === "FILED" && (
            <p className="text-xs text-muted-foreground mt-2">
              Filed to <strong>{item.chosen_folder}</strong>{" "}
              {item.drive_file_id ? (
                <a
                  className="text-primary hover:underline ml-2"
                  href={`https://drive.google.com/file/d/${item.drive_file_id}/view`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Drive
                </a>
              ) : null}
            </p>
          )}

          {(item.state === "FAILED_OCR" ||
            item.state === "FAILED_CLASSIFY" ||
            item.state === "FAILED_DRIVE_UPLOAD") && (
            <>
              <p className="text-sm text-destructive/80 mt-2">
                {item.failure_message || item.state}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retry.isPending}
                  onClick={() => retry.mutate()}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                  Retry
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-muted-foreground"
                  disabled={del.isPending}
                  onClick={() => del.mutate()}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Delete
                </Button>
              </div>
            </>
          )}

          {item.state === "FAILED_DRIVE_AUTH" && (
            <FailedDriveAuthRecovery
              fileId={item.file_id}
              versionRef={versionRef}
            />
          )}
        </div>
      </div>
    </article>
  );
}

// ──────────────────────────────────────────────────────────────────────
// FAILED_DRIVE_AUTH recovery — Drive re-grant + automatic retry
// ──────────────────────────────────────────────────────────────────────

/* When an item's Drive upload fails because the user's Google Drive
 * refresh-token was revoked (or expired beyond rotation), the row sits
 * in FAILED_DRIVE_AUTH waiting for the user to grant Drive access
 * again. The previous "Sign in again" button was just `<a href="/">`
 * which only bounced you back to home — useless when your PWA session
 * cookie is still valid (the home view doesn't re-prompt for Drive in
 * that case).
 *
 * This component actually does the right thing:
 *
 *   1. Click "Sign in again" → opens the Google OAuth popup with
 *      prompt=consent + access_type=offline, which re-issues a fresh
 *      refresh_token (and writes it to auth_tokens with revoked_at=NULL).
 *   2. On popup success → fires REAUTH_DONE event via PATCH
 *      /api/ihub/{file_id}, which the backend uses to flip the row
 *      from FAILED_DRIVE_AUTH back to CONFIRMED so the cron sweeper
 *      retries the Drive upload on the next pass.
 *   3. Invalidates the inbox-items query so the card updates locally
 *      without waiting for the SSE round-trip.
 */
function FailedDriveAuthRecovery({
  fileId,
  versionRef,
}: {
  fileId: string;
  versionRef: React.MutableRefObject<number>;
}) {
  const qc = useQueryClient();
  const startAuth = useStartAuth();
  const reauth = useMutation({
    mutationFn: async () => {
      // Step 1 — pop the OAuth window; resolves on postMessage success.
      await startAuth.mutateAsync();
      // Step 2 — tell backend to re-queue the upload.
      await patchItem(fileId, {
        event: "REAUTH_DONE",
        expected_version: versionRef.current,
      });
    },
    onSuccess: () => {
      toast.success("Drive reconnected — retrying upload");
      // Refetch the inbox listing + per-item view + session/Drive status.
      qc.invalidateQueries({ queryKey: ["ihub"] });
      qc.invalidateQueries({ queryKey: queryKeys.ihubItem(fileId) });
      qc.invalidateQueries({ queryKey: queryKeys.session });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Sign-in failed: ${msg}`);
    },
  });
  return (
    <div className="mt-3">
      <p className="text-sm text-destructive/80 mb-2">
        Google Drive sign-in expired. Sign in again to file this doc.
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => reauth.mutate()}
        disabled={reauth.isPending}
      >
        {reauth.isPending ? "Reconnecting…" : "Sign in again"}
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Upload progress strip
// ──────────────────────────────────────────────────────────────────────

function UploadStrip({ progresses }: { progresses: UploadProgress[] }) {
  if (progresses.length === 0) return null;
  return (
    <div className="mb-4 space-y-1.5">
      {progresses.map((p) => (
        <div
          key={p.file_id}
          className={cn(
            "flex items-center justify-between rounded-md border px-3 py-1.5 text-xs",
            p.phase === "failed"
              ? "bg-destructive/5 border-destructive/30 text-destructive"
              : p.phase === "done"
                ? "bg-success/5 border-success/30 text-success"
                : "bg-muted/30 border-border text-muted-foreground",
          )}
        >
          <span className="truncate">{p.filename}</span>
          <span className="ml-3 shrink-0">
            {p.phase === "hashing" && "Hashing…"}
            {p.phase === "creating" && "Submitting…"}
            {p.phase === "uploading" && `Uploading ${p.percent ?? 0}%`}
            {p.phase === "finalizing" && "Verifying…"}
            {p.phase === "done" && "Queued"}
            {p.phase === "failed" && (p.error || "Failed")}
          </span>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// View
// ──────────────────────────────────────────────────────────────────────

function IHubView() {
  const itemsQuery = useQuery({
    queryKey: queryKeys.ihub("ihub"),
    queryFn: () => listItems("ihub"),
  });

  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [reviewingItem, setReviewingItem] = useState<IhubItem | null>(null);
  const [multiScanOpen, setMultiScanOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  // Bulk-selection state. Stored as a Set of file_ids; the matching
  // version+state for each is pulled from the items list at delete
  // time (avoids stale-version drift while the user is selecting).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // 2-tap inline bulk-delete confirmation. window.confirm() is
  // unreliable in iOS Safari standalone-PWA mode — it sometimes
  // resolves to false immediately without showing a dialog, which made
  // the bulk-delete button look broken. The inline pattern (button
  // morphs into "Confirm?" + Cancel on first tap, commits on second)
  // sidesteps the native dialog entirely.
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);
  const qc = useQueryClient();

  /**
   * Stitch multiple images into ONE PDF client-side, then upload as
   * a single iHub item. Called from the MultiScanModal after the user
   * has accumulated N pages and tapped Finish.
   *
   * The OLD direct-from-file-input version was broken on iOS: the
   * camera input only ever returns 1 file per tap, so "Multi-scan"
   * silently degraded to single-scan. The modal flow drives the
   * per-page camera loop so we actually receive N files here.
   */
  const handleMultiScan = async (images: File[]) => {
    if (images.length === 0) return;

    // Single-page case still goes through this path (stitched as a
    // 1-page PDF). Old code fell through to handleFiles, which kept
    // the original JPEG — but the user explicitly opened Multi-scan,
    // so they want a PDF. Keeps the storage decision tree predictable:
    //   Scan tile        → image stays JPEG (fast path eligible)
    //   Multi-scan tile  → always PDF (one or N pages)

    setMultiScanOpen(false);
    const stitchId = crypto.randomUUID();
    const stitchFilename = defaultScanFilename();

    setUploads((prev) => [
      ...prev,
      {
        file_id: stitchId,
        filename: `${stitchFilename} (stitching ${images.length} pages…)`,
        phase: "hashing",
      },
    ]);

    try {
      const result = await stitchImagesToPdf(images);
      const pdfFile = pdfBlobToFile(result.blob, stitchFilename);

      // Drop the placeholder; the uploadOne call below pushes its own
      // progress with a fresh file_id.
      setUploads((prev) => prev.filter((p) => p.file_id !== stitchId));

      // Now use the same single-file path with the stitched PDF.
      const fileList: FileList = {
        0: pdfFile,
        length: 1,
        item: (i: number) => (i === 0 ? pdfFile : null),
        [Symbol.iterator]: function* () {
          yield pdfFile;
        },
      } as unknown as FileList;
      await handleFiles(fileList);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Stitch failed";
      setUploads((prev) =>
        prev.map((p) =>
          p.file_id === stitchId
            ? {
                ...p,
                filename: stitchFilename,
                phase: "failed",
                error: msg,
              }
            : p,
        ),
      );
      setTimeout(() => {
        setUploads((prev) => prev.filter((p) => p.file_id !== stitchId));
      }, 8_000);
    }
  };

  const handleFiles = async (
    files: FileList,
    userIntent?: "Photo" | "Document",
  ) => {
    for (const file of Array.from(files)) {
      const validation = validateFile(file);
      if (!validation.ok) {
        // Surface a one-shot error row for this file.
        const fake_id = crypto.randomUUID();
        setUploads((prev) => [
          ...prev,
          {
            file_id: fake_id,
            filename: file.name,
            phase: "failed",
            error: validation.reason,
          },
        ]);
        // Auto-clear after 8s.
        setTimeout(() => {
          setUploads((prev) => prev.filter((p) => p.file_id !== fake_id));
        }, 8_000);
        continue;
      }

      try {
        await uploadOne(
          file,
          (p) => {
            setUploads((prev) => {
              const idx = prev.findIndex((x) => x.file_id === p.file_id);
              if (idx < 0) return [...prev, p];
              const copy = prev.slice();
              copy[idx] = p;
              return copy;
            });
          },
          { userIntent },
        );
        // After finalize, force the list to refetch. The SSE
        // item_created event will also invalidate, so this is belt
        // + braces.
        qc.invalidateQueries({ queryKey: ["ihub"] });
        // Clear the progress row after a short delay.
        setTimeout(() => {
          setUploads((prev) =>
            prev.filter(
              (p) => !(p.phase === "done" && Date.now() - 0 > 0),
            ),
          );
        }, 3_000);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Upload failed";
        setUploads((prev) => {
          const idx = prev.findIndex((x) => x.filename === file.name);
          const update: UploadProgress = {
            file_id: prev[idx]?.file_id ?? crypto.randomUUID(),
            filename: file.name,
            phase: "failed",
            error: msg,
          };
          if (idx < 0) return [...prev, update];
          const copy = prev.slice();
          copy[idx] = update;
          return copy;
        });
      }
    }
  };

  const items = itemsQuery.data?.items ?? [];

  // If a reviewing item exists, find the latest copy from the live list
  // so SSE updates (lock changes, field edits from another device) reach
  // the modal automatically.
  const liveReviewing = reviewingItem
    ? items.find((i) => i.file_id === reviewingItem.file_id) ?? reviewingItem
    : null;

  // ────────────────────────────────────────────────
  // Bulk selection helpers
  // ────────────────────────────────────────────────

  const toggleSelect = (file_id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(file_id)) next.delete(file_id);
      else next.add(file_id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(items.map((i) => i.file_id)));
  const clearSelection = () => setSelected(new Set());

  // Drop selections that are no longer in the list (e.g. SSE deleted
  // an item that another device removed). Keeps the count honest.
  useEffect(() => {
    setSelected((prev) => {
      const live = new Set(items.map((i) => i.file_id));
      let dirty = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else dirty = true;
      }
      return dirty ? next : prev;
    });
  }, [items]);

  // If the selection drops to zero (manual clear, deletion completed),
  // exit the confirm-delete state so the toolbar doesn't stick.
  useEffect(() => {
    if (selected.size === 0 && confirmingBulkDelete) {
      setConfirmingBulkDelete(false);
    }
  }, [selected.size, confirmingBulkDelete]);

  // Commits the bulk delete without a native confirm dialog. Caller
  // (the toolbar's two-tap pattern) is responsible for guarding intent.
  const bulkDeleteCommit = async () => {
    if (selected.size === 0 || bulkBusy) return;
    const ids = [...selected];
    setConfirmingBulkDelete(false);
    setBulkBusy(true);
    const byId = new Map(items.map((i) => [i.file_id, i]));
    // Run in parallel — D1 handles the small fan-out fine. Per-item
    // failures (e.g. CONFIRMED grace window for a recently-confirmed
    // doc) are swallowed individually so one bad row doesn't block
    // the rest.
    const results = await Promise.allSettled(
      ids.map(async (id) => {
        const item = byId.get(id);
        if (!item) throw new Error("item not in list");
        await patchItem(id, {
          event: "USER_DELETE",
          expected_version: item.version,
        });
      }),
    );
    const fails = results.filter((r) => r.status === "rejected").length;
    setBulkBusy(false);
    setSelected(new Set());
    qc.invalidateQueries({ queryKey: ["ihub"] });
    if (fails === 0) {
      toast.success(
        `Deleted ${ids.length} ${ids.length === 1 ? "item" : "items"}`,
      );
    } else if (fails === ids.length) {
      toast.error(
        `Couldn't delete ${fails} ${fails === 1 ? "item" : "items"}`,
        {
          description:
            "Try again in a few seconds — items may be mid-Drive-upload.",
        },
      );
    } else {
      toast.warning(
        `Deleted ${ids.length - fails} of ${ids.length}`,
        {
          description: `${fails} couldn't be deleted (likely mid-Drive-upload). Retry?`,
        },
      );
    }
  };

  const allSelected = items.length > 0 && selected.size === items.length;

  // Time-aware greeting. Subtle, but it makes the empty state feel
  // like a personal home instead of a database list.
  const session = useSession();
  // Prefer the display_name the user set in Settings → Profile. Fall
  // back to deriving a friendly first name from the email local-part
  // (s0123uma → Uma) so brand-new users still get a real greeting
  // before they've filled in their name.
  const firstName = (() => {
    const display = session.data?.display_name?.trim();
    if (display) {
      // Use FIRST word of the display name for the greeting so
      // "Good morning, Uma Seetharaman" → "Good morning, Uma" (the
      // full name still appears in Settings).
      return display.split(/\s+/)[0];
    }
    const local = session.data?.email?.split("@")[0] ?? "";
    if (!local) return "";
    const cleaned = local.replace(/^\d+/, "").replace(/[._-].*$/, "");
    return cleaned || local;
  })();
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 5) return "Up late";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 22) return "Good evening";
    return "Good night";
  })();
  const reviewCount = items.filter((i) => i.state === "REVIEW").length;
  const processingCount = items.filter(
    (i) => i.state === "PROCESSING" || i.state === "NEW",
  ).length;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-8 py-8">
      {/* Header — friendlier greeting + at-a-glance status pills
          ("3 ready to review · 1 processing") instead of a bare count.
          On mobile the pills wrap below the greeting. */}
      <header className="mb-6 space-y-2">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h1 className="font-display text-3xl">
            {greeting}
            {firstName && (
              <>
                ,{" "}
                <span className="text-primary capitalize">{firstName}</span>
              </>
            )}
          </h1>
          {!itemsQuery.isLoading && items.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {items.length} in iHub
            </span>
          )}
        </div>
        {/* Quick-action row — always visible at top of iHub home:
            • Refresh: refetch inbox + folders (when data looks stale)
            • Debug: pull /api/debug snapshot inline so you can see
              what the server thinks
            • Version pill: jumps to Settings → About / Force update
            One row, mobile-friendly. */}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground/80">
          <button
            type="button"
            onClick={() => {
              qc.invalidateQueries({ queryKey: ["ihub"] });
              qc.invalidateQueries({ queryKey: queryKeys.folders });
              toast.success("Refreshed", {
                description: "Inbox + folders re-fetched.",
                duration: 1500,
              });
            }}
            className="inline-flex items-center gap-1 hover:text-foreground transition px-2 py-1 rounded hover:bg-secondary"
            title="Refetch inbox + folders"
          >
            <RefreshCw className="h-3 w-3" />
            <span>Refresh</span>
          </button>
          <button
            type="button"
            onClick={() => setDebugOpen(true)}
            className="inline-flex items-center gap-1 hover:text-foreground transition px-2 py-1 rounded hover:bg-secondary"
            title="Show server debug snapshot"
          >
            <AlertTriangle className="h-3 w-3" />
            <span>Debug</span>
          </button>
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 hover:text-foreground transition px-2 py-1 rounded hover:bg-secondary ml-auto"
            title={`Build ${BUILD_VERSION} — tap to view in Settings`}
          >
            <span>build</span>
            <code className="font-mono">{BUILD_VERSION.split(" ")[0]}</code>
          </Link>
        </div>
        {/* Status row — only shows if there's actually anything to say */}
        {(reviewCount > 0 || processingCount > 0) && (
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {reviewCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-success/10 text-success border border-success/20">
                <CheckCircle2 className="h-3 w-3" />
                {reviewCount} ready to review
              </span>
            )}
            {processingCount > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-warning/10 text-warning-foreground border border-warning/30">
                <Loader2 className="h-3 w-3 animate-spin" />
                {processingCount} processing
              </span>
            )}
          </div>
        )}
      </header>

      {/* Action tiles — three intents, each clearly separate:
          • Add   → file picker (existing files on device). Auto-classifies.
          • Photo → camera, single capture, forced Photo intent (no OCR,
                    no LLaVA classification). The only way to guarantee
                    a memory snapshot doesn't get misread as a donation.
          • Scan  → camera, multi-page modal. Hit Finish after 1 page
                    for a single-page scan; or capture N pages and we
                    stitch to a single PDF. Always Document intent.

          We collapsed the previous "Scan Document" tile into Scan
          because the multi-scan modal already handles single-page
          (just tap Finish after one capture). One less button. */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6">
        <ActionTile
          icon={Upload}
          title="Add"
          subtitle="files & photos"
          accept="image/*,application/pdf"
          multiple
          onFiles={(f) => handleFiles(f)}
        />
        <ActionTile
          icon={ImageIcon}
          title="Photo"
          subtitle="memory snapshot"
          accept="image/*"
          capture="environment"
          onFiles={(f) => handleFiles(f, "Photo")}
        />
        <button
          type="button"
          onClick={() => setMultiScanOpen(true)}
          className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-6 text-center hover:border-primary/40 hover:shadow-md hover:-translate-y-0.5 transition"
        >
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 transition">
            <Camera className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium text-sm">Scan</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              1+ pages → 1 PDF
            </div>
          </div>
        </button>
      </div>

      <UploadStrip progresses={uploads} />

      {/* "Select all N" affordance — visible BEFORE the user picks
          anything, so it's discoverable from a cold start. Outline
          button (not ghost) so it reads as a clickable affordance,
          not flat text. Once any item is selected, the sticky bulk
          toolbar below takes over with its own Select all/Clear. */}
      {items.length > 1 && selected.size === 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={selectAll}
            className="h-9 border-primary/40 text-foreground hover:bg-primary/10 hover:border-primary/60 font-medium"
          >
            <span className="inline-block w-4 h-4 mr-2 rounded border-2 border-current" />
            Select all {items.length}
          </Button>
          <span className="text-xs text-muted-foreground">
            or tap the file icon on a card
          </span>
        </div>
      )}

      {/* Bulk selection toolbar — appears when ≥1 item is selected.
          Sticky to the top of the list area on mobile so it stays
          visible as the user scrolls through and picks.
          When confirmingBulkDelete is true, the toolbar morphs into
          a confirm strip — "Delete N? · Yes · Cancel" — so we don't
          rely on window.confirm (which iOS standalone PWAs silently
          drop, breaking the bulk-delete path). */}
      {selected.size > 0 && (
        <div className="sticky top-14 z-20 -mx-4 sm:-mx-8 mb-4 px-4 sm:px-8 py-2.5 bg-primary text-primary-foreground shadow-md flex items-center gap-2 flex-wrap">
          {confirmingBulkDelete ? (
            <>
              <span className="text-sm font-medium">
                Delete {selected.size}{" "}
                {selected.size === 1 ? "item" : "items"}? This can't be
                undone.
              </span>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="destructive"
                disabled={bulkBusy}
                onClick={bulkDeleteCommit}
                className="h-8"
              >
                {bulkBusy ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Yes, delete
                  </>
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => setConfirmingBulkDelete(false)}
                className="text-primary-foreground hover:bg-primary-foreground/10 h-8"
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm font-medium">
                {selected.size} selected
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={allSelected ? clearSelection : selectAll}
                className="text-xs h-8"
              >
                {allSelected ? "Clear" : "Select all"}
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                variant="destructive"
                disabled={bulkBusy}
                onClick={() => setConfirmingBulkDelete(true)}
                className="h-8"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete {selected.size}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                className="text-primary-foreground hover:bg-primary-foreground/10 h-8 w-8 p-0"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      )}

      {itemsQuery.isError ? (
        <div className="text-center py-12 text-destructive">
          <p>Couldn't load your iHub. Refresh the page?</p>
        </div>
      ) : items.length > 0 ? (
        <div className="space-y-3">
          {items.map((it) => (
            <ItemCard
              key={it.file_id}
              item={it}
              onOpenReview={setReviewingItem}
              selected={selected.has(it.file_id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      ) : !itemsQuery.isLoading ? (
        /* Empty state — warmer, less terminal-y. The Library link is
            a soft escape hatch when the user wants to browse what
            they've already filed instead of adding more. */
        <div className="text-center py-16 space-y-3">
          <div className="text-5xl mb-2">✨</div>
          <p className="text-foreground font-medium">Your inbox is clear.</p>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Tap <strong>Upload</strong>, <strong>Scan</strong>, or{" "}
            <strong>Multi-scan</strong> above to add a document. Or{" "}
            <Link to="/library" className="text-primary hover:underline">
              browse what's already filed
            </Link>
            .
          </p>
        </div>
      ) : null}

      {liveReviewing && (
        <ReviewDialog
          item={liveReviewing}
          open={true}
          onOpenChange={(open) => {
            if (!open) setReviewingItem(null);
          }}
        />
      )}

      <MultiScanModal
        open={multiScanOpen}
        onOpenChange={setMultiScanOpen}
        onFinish={(files) => {
          void handleMultiScan(files);
        }}
      />

      <DebugDialog open={debugOpen} onOpenChange={setDebugOpen} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Debug dialog — fetches /api/debug on open and renders the JSON inline
// so the user can see server state without leaving the page or copying
// browser cookies into curl. Read-only.
// ──────────────────────────────────────────────────────────────────────
function DebugDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [text, setText] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await fetch("/api/debug", {
        method: "GET",
        credentials: "include",
        headers: { accept: "application/json" },
      });
      const body = await r.text();
      if (!r.ok) {
        setErr(`HTTP ${r.status} — ${body.slice(0, 200)}`);
      } else {
        try {
          setText(JSON.stringify(JSON.parse(body), null, 2));
        } catch {
          setText(body);
        }
      }
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };

  // Auto-load on open.
  useEffect(() => {
    if (open) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 sm:px-6 pt-4 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-base font-semibold inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Server debug snapshot
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            What the server thinks is going on. Build, auth, inbox state,
            recent logs. Read-only; safe to share for diagnosis.
          </p>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto px-4 sm:px-6 py-3 scroll-visible">
          {busy && (
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          )}
          {err && (
            <p className="text-sm text-destructive" role="alert">
              {err}
            </p>
          )}
          {!busy && !err && text && (
            <pre className="text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed">
              {text}
            </pre>
          )}
        </div>
        <DialogFooter className="px-4 sm:px-6 py-3 border-t border-border shrink-0 flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => {
              void navigator.clipboard
                .writeText(text)
                .then(() => toast.success("Copied to clipboard"))
                .catch(() => toast.error("Copy failed"));
            }}
            disabled={!text || busy}
            className="w-full sm:w-auto"
          >
            Copy JSON
          </Button>
          <Button
            variant="outline"
            onClick={() => void load()}
            disabled={busy}
            className="w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto sm:ml-auto"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
